import React, { useState, useEffect, useRef } from 'react';
import { Tag, TagType } from '../../types/tag';
import { tagService } from '../../services/tagService';
import { X, Plus, Search, Loader2, Sparkles } from 'lucide-react';

interface SoftTagInputProps {
    type: TagType;
    selectedTags: Tag[]; // Full Tag objects needed for display
    onChange: (tags: Tag[]) => void;
    label?: string;
    placeholder?: string;
    maxTags?: number;
    showTopTags?: boolean; // New prop to show top 10 tags
}

export const SoftTagInput: React.FC<SoftTagInputProps> = ({
    type,
    selectedTags,
    onChange,
    label,
    placeholder = "Add a tag...",
    maxTags,
    showTopTags = false
}) => {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState<Tag[]>([]);
    const [popularTags, setPopularTags] = useState<Tag[]>([]);
    const [loading, setLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Load popular tags if enabled
    useEffect(() => {
        if (showTopTags) {
            tagService.getTopTags(type, 10).then(setPopularTags);
        }
    }, [type, showTopTags]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (inputValue.trim().length > 0) {
                setLoading(true);
                try {
                    const results = await tagService.searchTags(inputValue, type);
                    // Filter out already selected tags
                    const unselectedResults = results.filter(
                        t => !selectedTags.some(selected => selected.id === t.id)
                    );
                    setSuggestions(unselectedResults);
                    setShowSuggestions(true);
                } catch (error) {
                    console.error("Search failed", error);
                } finally {
                    setLoading(false);
                }
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [inputValue, type, selectedTags]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleAddTag = (tag: Tag) => {
        if (maxTags && selectedTags.length >= maxTags) return;
        // Check if already selected (id check)
        if (selectedTags.some(t => t.id === tag.id)) return;

        onChange([...selectedTags, tag]);
        setInputValue('');
        setSuggestions([]);
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const handleCreateTag = async () => {
        if (!inputValue.trim()) return;
        setLoading(true);
        try {
            // Check if it already exists in suggestions to avoid dupe creation
            const existing = suggestions.find(s => s.name.toLowerCase() === inputValue.trim().toLowerCase());
            if (existing) {
                handleAddTag(existing);
            } else {
                const newTag = await tagService.createTag(inputValue, type);
                handleAddTag(newTag);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveTag = (tagId: string) => {
        onChange(selectedTags.filter(t => t.id !== tagId));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (suggestions.length > 0) {
                // Option 1: Add first suggestion
                handleAddTag(suggestions[0]);
            } else {
                // Option 2: Create new if allowed
                handleCreateTag();
            }
        } else if (e.key === 'Backspace' && inputValue === '' && selectedTags.length > 0) {
            // Remove last tag
            handleRemoveTag(selectedTags[selectedTags.length - 1].id);
        }
    };

    return (
        <div className="space-y-3" ref={wrapperRef}>
            {label && (
                <label className="text-sm font-medium text-slate-700 block">
                    {label}
                    {maxTags && <span className="text-slate-400 font-normal ml-2">({selectedTags.length}/{maxTags})</span>}
                </label>
            )}

            <div className="relative">
                <div
                    className="flex flex-wrap gap-2 p-2 min-h-[42px] bg-white border border-slate-200 rounded-lg focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-400 transition-all cursor-text"
                    onClick={() => inputRef.current?.focus()}
                >
                    {selectedTags.map(tag => (
                        <span
                            key={tag.id}
                            className="inline-flex items-center px-2 py-1 rounded bg-blue-50 text-blue-700 text-sm border border-blue-100"
                        >
                            {tag.name}
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleRemoveTag(tag.id); }}
                                className="ml-1 hover:text-blue-900 focus:outline-none"
                            >
                                <X size={14} />
                            </button>
                        </span>
                    ))}

                    <input
                        ref={inputRef}
                        type="text"
                        className="flex-1 min-w-[120px] outline-none bg-transparent text-sm py-1"
                        placeholder={selectedTags.length === 0 ? placeholder : ""}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={maxTags ? selectedTags.length >= maxTags : false}
                        autoComplete="off"
                    />
                </div>

                {/* Suggestions Dropdown */}
                {showSuggestions && (
                    <div className="absolute top-full left-0 z-50 w-full max-w-md mt-1 bg-white rounded-lg shadow-xl border border-slate-100 overflow-hidden max-h-60 overflow-y-auto">
                        {loading && (
                            <div className="p-3 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
                                <Loader2 size={16} className="animate-spin" /> 搜索中...
                            </div>
                        )}

                        {!loading && suggestions.length > 0 && (
                            <ul>
                                {suggestions.map((tag, index) => (
                                    <li
                                        key={tag.id}
                                        className="px-4 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700 flex items-center justify-between group"
                                        onClick={() => handleAddTag(tag)}
                                    >
                                        <span>
                                            {tag.name}
                                            <span className="text-xs text-gray-400 ml-2">({tag.usageCount || 0})</span>
                                        </span>
                                        <Plus size={14} className="opacity-0 group-hover:opacity-100 text-slate-400" />
                                    </li>
                                ))}
                            </ul>
                        )}

                        {!loading && inputValue && suggestions.length === 0 && (
                            <div
                                className="px-4 py-2 hover:bg-slate-50 cursor-pointer text-sm text-blue-600 flex items-center gap-2"
                                onClick={handleCreateTag}
                            >
                                <Plus size={14} />
                                创建 "{inputValue}"
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Popular Tags Display - Moved outside relative container to stay below dropdown when closed, but obscured when open? 
               actually if dropdown is absolute z-50, it will cover popular tags. Correct. 
            */}
            {showTopTags && popularTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-xs text-gray-400 flex items-center gap-1 self-center mr-1">
                        <Sparkles size={12} /> 热门:
                    </span>
                    {popularTags.map(tag => {
                        const isSelected = selectedTags.some(t => t.id === tag.id);
                        if (isSelected) return null;
                        return (
                            <button
                                key={tag.id}
                                type="button"
                                onClick={() => handleAddTag(tag)}
                                className="text-xs px-2 py-1 bg-gray-50 text-gray-600 rounded-full hover:bg-gray-100 hover:text-blue-600 border border-transparent hover:border-blue-100 transition-colors"
                            >
                                {tag.name}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
